import React, {
  forwardRef,
  useEffect, useMemo, useRef, useState,
} from 'react';

import { SearchRounded } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import { debounce } from 'lodash';

import TextField from '../TextField';

import {
  ExpandContainer,
  SearchButton,
  TopContainer,
} from './index.styles';

const DROPDOWN = 'dropdown';
type DROPDOWN_TYPE = 'dropdown';
type STANDARD_TYPE = 'standard'

export interface DropdownSearchFieldProps {
  options: any[],
  getOptionLabel: (x: any) => string,
  getOptionKey: (x: any) => string,
  placeholder?: string,
  loading?: boolean,
  onSearch?: (searchStr: string) => void,
  autoFilter?: boolean,
  onSelect?: (x: any) => void,
  clearOnSelect?: boolean,
  onBlur?: () => void,
  renderOption?: (props: any, option: any) => React.ReactNode,
}

const DropdownSearchField = forwardRef(({ options = [],
  getOptionLabel,
  getOptionKey,
  placeholder,
  loading,
  onSearch = () => {},
  onSelect = () => {},
  onBlur = () => {},
  autoFilter = false,
  renderOption,
  clearOnSelect = false }: DropdownSearchFieldProps, ref) => {
  const [searchStr, setSearchStr] = useState<string>('');

  const onChangeDebounced = useMemo(
    () => debounce((v) => {
      onSearch(v);
    }, 500),
    []);

  useEffect(() => {
    onChangeDebounced(searchStr);
  }, [searchStr]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(opt, val) => getOptionKey(opt) === getOptionKey(val)}
      clearOnBlur={clearOnSelect}
      filterOptions={autoFilter ? undefined : (x) => x}
      loading={loading}
      onInputChange={(e, v) => setSearchStr(v)}
      onChange={(e, v) => onSelect(v)}
      inputValue={searchStr}
      ref={ref}
      onBlur={onBlur}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          inputProps={{ ...params.inputProps }}
          placeholder={placeholder || 'Search...'}
          autoFocus
          ref={params.InputProps.ref}
        />
      )}
    />
  );
});

interface StandardSearchFieldProps {
  placeholder?: string,
  onSearch?: (searchStr: string) => void,
  onBlur?: () => void,
}

const StandardSearchField = forwardRef(({
  placeholder,
  onSearch = () => {},
  onBlur = () => {},
}: StandardSearchFieldProps, ref) => {
  const [searchStr, setSearchStr] = useState<string>('');

  const onChangeDebounced = useMemo(
    () => debounce((v) => {
      setSearchStr(v);
    }, 500),
    []);

  useEffect(() => {
    onSearch(searchStr);
  }, [searchStr]);

  return (
    <TextField
      ref={ref}
      placeholder={placeholder || 'Search...'}
      onChange={(e) => onChangeDebounced(e.target.value)}
      onBlur={onBlur}
    />
  );
});

interface SearchFieldPropsForStandard extends StandardSearchFieldProps {
  type?: STANDARD_TYPE,
  collapseEnabled?: boolean,
}

interface SearchFieldPropsForDropdown extends DropdownSearchFieldProps {
  type?: DROPDOWN_TYPE,
  collapseEnabled?: boolean,
}

const SearchField = (
  { type,
    collapseEnabled = false,
    ...rest } : SearchFieldPropsForStandard | SearchFieldPropsForDropdown) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const [expanded, setExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (expanded && ref.current) {
      ref.current.focus();
    }
  }, [expanded, ref]);

  return (
    <TopContainer>
      {collapseEnabled && !expanded && (
        <SearchButton
          onClick={() => setExpanded(true)}
          $expand={expanded}
        >
          <SearchRounded />
        </SearchButton>
      )}
      <ExpandContainer $expand={!collapseEnabled || expanded}>
        {type === DROPDOWN ? (
          <DropdownSearchField
            ref={ref}
            {...rest as SearchFieldPropsForDropdown}
          />
        ) : (
          <StandardSearchField
            ref={ref}
            {...rest as SearchFieldPropsForStandard}
          />
        )}
      </ExpandContainer>
    </TopContainer>
  );
};

export default SearchField;
